import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { restoreSession } from '../store/authSlice';
import { loadProjects, resetProjectState } from '../store/project/projectSlice';
import { resetWorkflow } from '../store/workflow/workflowSlice';
import SignIn from './auth/SignIn';
import { useTranslation } from 'react-i18next';

/**
 * 앱 진입 게이트 — 세션 확인 → 작업 시스템 확정 → 프로젝트 목록 로드.
 *
 * 로그인 경로가 여러 개여도(폼·나중에 OAuth 등) 새로고침 후에도 반드시 지나는 공통 지점은 여기뿐이라
 * activeSysId 확정(ensureActiveSystem)을 여기에 둔다 — 오픈데이터모델러 AppInitializer 선례.
 * 이걸 빠뜨리면 QBase 읽기 스코프가 빈 축(AND 1=0) 위에서 0건을 돌려준다.
 */
const AppInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const status = useSelector((s: RootState) => s.auth.status);
  const activeSysId = useSelector((s: RootState) => s.auth.activeSysId);

  useEffect(() => { dispatch(restoreSession()); }, [dispatch]);

  // 인증되면 프로젝트 로드, 로그아웃되면 메모리 상태 비움 (다음 사용자에게 이전 캔버스가 보이면 안 된다)
  useEffect(() => {
    if (status === 'authenticated') {
      dispatch(loadProjects());
    } else if (status === 'anonymous') {
      dispatch(resetProjectState());
      dispatch(resetWorkflow());
    }
  }, [status, dispatch]);

  if (status === 'idle' || status === 'checking') {
    return <div className="flex items-center justify-center h-screen text-gray-500">{t('layout:auth.sessionCheck')}</div>;
  }
  if (status !== 'authenticated') {
    return <SignIn />;
  }
  if (!activeSysId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="max-w-md bg-red-50 border border-red-200 rounded p-4 text-sm text-red-700">
          {t('layout:auth.noSystem')}
        </div>
      </div>
    );
  }
  return <>{children}</>;
};

export default AppInitializer;
